import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Home,
  Briefcase,
  FolderOpen,
  Users,
  MessageSquare,
  Info,
  type LucideIcon
} from "lucide-react";
import Sidebar from './Sidebar';
import { useSound } from '../hooks/useSound';

interface NavItem {
  name: string;
  url: string;
  icon: LucideIcon;
}

interface NavBarProps {
  items?: NavItem[];
  className?: string;
  defaultActive?: string;
}

const defaultNavItems: NavItem[] = [
  { name: "Home", url: "/", icon: Home },
  { name: "About", url: "/about", icon: Info },
  { name: "Services", url: "/services", icon: Briefcase },
  { name: "Archive", url: "/Archive", icon: FolderOpen },
  { name: "Testimonials", url: "/testimonials", icon: Users },
  { name: "Contact", url: "/contact", icon: MessageSquare },
];

export function AnimeNavBar({
  items = defaultNavItems,
  className,
  defaultActive = "Home"
}: NavBarProps) {
  const { playClick } = useSound();
  const navigate = useNavigate();
  const location = useLocation();
  const [mounted, setMounted] = useState(false);
  const [hoveredTab, setHoveredTab] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>(defaultActive);

  // New States for Scroll and Sidebar
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isCalendlyOpen, setIsCalendlyOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    setMounted(true);

    const handleScroll = () => {
      const scrolled = window.scrollY > 50;
      setIsScrolled(scrolled);
      // Close sidebar if we scroll back to top
      if (!scrolled) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Set active tab based on current route
    const currentItem = items.find(item => item.url === location.pathname);
    if (currentItem) {
      setActiveTab(currentItem.name);
    } else if (location.pathname === "/") {
      setActiveTab("Home");
    }
  }, [location.pathname, items]);



  // Determine if the current page has a light background
  const isLightPage = ['/about', '/services', '/Archive', '/contact'].includes(location.pathname) || location.pathname.startsWith('/services/');

  // Don't show on dashboard or admin pages
  if (location.pathname === '/dashboard' || location.pathname.startsWith('/admin') || location.pathname === '/login') {
    return null;
  }

  if (!mounted) return null;

  return (
    <>
      {/* Sidebar Component - Controlled by isSidebarOpen */}
      <Sidebar
        isOpen={isSidebarOpen}
        onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        hideTrigger={true}
      />

      {/* Floating Action Button - Visible when Scrolled (Desktop Only) */}
      <AnimatePresence>
        {isScrolled && !isMobile && (
          <motion.button
            initial={{ scale: 0, opacity: 0, rotate: 90 }}
            animate={{
              scale: 1,
              opacity: 1,
              rotate: isSidebarOpen ? 45 : 0
            }}
            exit={{ scale: 0, opacity: 0, rotate: 90 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            onClick={() => { playClick(); setIsSidebarOpen(!isSidebarOpen); }}
            className={`fixed top-4 right-4 sm:top-5 sm:right-5 lg:top-7 lg:right-6 z-[10001] w-12 h-12 sm:w-14 sm:h-14 lg:w-17 lg:h-17 bg-white cursor-pointer rounded-full flex items-center justify-center shadow-md transition-all duration-300 group lg:hover:scale-105 ${isSidebarOpen ? 'bg-gray-100' : 'lg:hover:bg-black'}`}
            aria-label={isSidebarOpen ? "Close menu" : "Open menu"}
          >
            <div className="transition-transform duration-300 ease-out">
              <svg
                stroke="currentColor"
                fill="currentColor"
                strokeWidth="0"
                viewBox="0 0 24 24"
                aria-hidden="true"
                className={`w-5 h-5 sm:w-6 sm:h-6 lg:w-10 lg:h-10 transition-colors duration-300 ${isSidebarOpen ? 'text-black' : 'text-black lg:group-hover:text-white'}`}
                height="1em"
                width="1em"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path fillRule="evenodd" d="M12 3.75a.75.75 0 0 1 .75.75v6.75h6.75a.75.75 0 0 1 0 1.5h-6.75v6.75a.75.75 0 0 1-1.5 0v-6.75H4.5a.75.75 0 0 1 0-1.5h6.75V4.5a.75.75 0 0 1 .75-.75Z" clipRule="evenodd"></path>
              </svg>
            </div>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Persistent Mobile Sidebar Toggle (Always visible on mobile) */}
      {isMobile && !isCalendlyOpen && (
        <button
          onClick={() => { playClick(); setIsSidebarOpen(!isSidebarOpen); }}
          className={`fixed right-6 top-20 pointer-events-auto w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 z-[10005] border backdrop-blur-md
            ${isLightPage
              ? "bg-white border-black/10 text-black active:bg-gray-100"
              : "bg-white border-white/20 text-black active:bg-gray-200"
            }
          `}
          aria-label="Toggle menu"
        >
          <div className={`transition-transform duration-300 ${isSidebarOpen ? 'rotate-45' : 'rotate-0'}`}>
            <svg
              stroke="currentColor"
              fill="currentColor"
              strokeWidth="0"
              viewBox="0 0 24 24"
              className="w-6 h-6"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path fillRule="evenodd" d="M12 3.75a.75.75 0 0 1 .75.75v6.75h6.75a.75.75 0 0 1 0 1.5h-6.75v6.75a.75.75 0 0 1-1.5 0v-6.75H4.5a.75.75 0 0 1 0-1.5h6.75V4.5a.75.75 0 0 1 .75-.75Z" clipRule="evenodd"></path>
            </svg>
          </div>
        </button>
      )}

      {/* Original Navbar - Visible when NOT Scrolled AND Sidebar is Closed */}
      <AnimatePresence>
        {(!isSidebarOpen && (!isScrolled || isMobile)) && (
          <motion.div
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -100, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed top-5 left-0 right-0 z-[9999] pointer-events-none"
          >
            {/* Logo (Absolute top-left, both mobile and desktop) */}
            <AnimatePresence>
              {(!isScrolled || !isMobile) && (
                <motion.a
                  href="/"
                  initial={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={(e) => {
                    e.preventDefault();
                    if (location.pathname === '/') {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    } else {
                      navigate('/');
                    }
                  }}
                  className="absolute left-6 top-2 md:-left-0 md:top-4 pointer-events-auto transition-transform hover:scale-105 z-50 flex"
                >
                  <img
                    src={isLightPage ? "/Nuvio_BLACK_logobg.png" : "/New_Logoicon.png"}
                    alt="Nuviosites"
                    className="w-14 h-auto md:w-32 md:max-h-16 object-contain drop-shadow-lg"
                  />
                </motion.a>
              )}
            </AnimatePresence>

            {/* Let's Talk Button */}
            <button
              onClick={() => { playClick(); setIsCalendlyOpen(true); }}
              className={`absolute right-6 top-2 md:top-9 md:right-12 pointer-events-auto text-xs md:text-sm px-4 md:px-6 py-2 md:py-2.5 rounded-lg font-medium shadow-md backdrop-blur-md transition-all duration-300 z-50 border
                ${isLightPage
                  ? "text-black bg-white border-black/10 hover:bg-black hover:text-white"
                  : "text-white bg-black border-white/20 hover:bg-white hover:text-black"
                }
              `}
            >
              Let's Talk
            </button>



            {/* Desktop Navbar container (Perfectly centered) */}
            <div className="hidden md:flex justify-center pt-6">
              <motion.div
                className={`pointer-events-auto flex items-center gap-2 md:gap-3 bg-black/50 border border-white/10 backdrop-blur-lg py-2 px-2 rounded-full shadow-lg relative ${className || ''}`}
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{
                  type: "spring",
                  stiffness: 260,
                  damping: 20,
                }}
              >
                {items.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeTab === item.name;
                  const isHovered = hoveredTab === item.name;

                  return (
                    <a
                      key={item.name}
                      href={item.url}
                      onClick={(e) => {
                        e.preventDefault();
                        playClick();
                        setActiveTab(item.name);
                        navigate(item.url);
                      }}
                      onMouseEnter={() => setHoveredTab(item.name)}
                      onMouseLeave={() => setHoveredTab(null)}
                      className={`
                        relative cursor-pointer text-sm font-semibold px-3 md:px-6 py-2 md:py-3 rounded-full transition-all duration-300
                        text-white/70 hover:text-white
                        ${isActive ? "text-white" : ""}
                      `}
                    >
                      {isActive && (
                        <motion.div
                          className="absolute inset-0 rounded-full -z-10 overflow-hidden"
                          initial={{ opacity: 0 }}
                          animate={{
                            opacity: [0.3, 0.5, 0.3],
                            scale: [1, 1.03, 1]
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut"
                          }}
                        >
                          <div className="absolute inset-0 bg-white/25 rounded-full blur-md" />
                          <div className="absolute inset-[-4px] bg-white/20 rounded-full blur-xl" />
                          <div className="absolute inset-[-8px] bg-white/15 rounded-full blur-2xl" />
                          <div className="absolute inset-[-12px] bg-white/5 rounded-full blur-3xl" />

                          <motion.div
                            className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
                            animate={{
                              x: ["-100%", "100%"]
                            }}
                            transition={{
                              duration: 3,
                              repeat: Infinity,
                              ease: "easeInOut"
                            }}
                          />
                        </motion.div>
                      )}

                      <motion.span
                        className="hidden md:inline relative z-10"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.2 }}
                      >
                        {item.name}
                      </motion.span>
                      <motion.span
                        className="md:hidden relative z-10"
                        whileHover={{ scale: 1.2 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <Icon size={18} strokeWidth={2.5} />
                      </motion.span>

                      <AnimatePresence>
                        {isHovered && !isActive && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            className="absolute inset-0 bg-white/10 rounded-full -z-10"
                          />
                        )}
                      </AnimatePresence>


                    </a>
                  );
                })}
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Calendly Modal */}
      <AnimatePresence>
        {isCalendlyOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[10002] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            onClick={() => setIsCalendlyOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative w-full max-w-4xl h-[80vh] bg-white rounded-2xl overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={() => setIsCalendlyOpen(false)}
                className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-black/10 hover:bg-black/20 text-black rounded-full transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>

              {/* Calendly Iframe */}
              <iframe
                src="https://calendly.com/nuviosites/30min"
                width="100%"
                height="100%"
                frameBorder="0"
                title="Schedule a meeting"
                className="w-full h-full"
              ></iframe>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default AnimeNavBar;
